<template>
	<transition name="toast-fade">
		<div v-show="visible" class="global-toast" :class="[type, position]" :style="customStyle">
			<div class="toast-content">
				<i v-if="showIcon && iconType" class="toast-icon" :class="iconType"></i>
				<span class="toast-text">{{ message }}</span>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: 'GlobalToast',
	data() {
		return {
			visible: false,
			message: '',
			type: 'default', // default, success, error, warning, loading
			position: 'center', // center, top, bottom
			duration: 2000,
			showIcon: true,
			customStyle: {},
			timer: null
		};
	},
	computed: {
		iconType() {
			const iconMap = {
				success: 'icon-success',
				error: 'icon-error',
				warning: 'icon-warning',
				loading: 'icon-loading'
			};
			return iconMap[this.type] || '';
		}
	},
	methods: {
		show(options) {
			// 清除之前的定时器
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}

			// 处理参数：如果是字符串，转为对象
			if (typeof options === 'string') {
				options = { message: options };
			}

			// 合并配置
			this.message = options.message || '';
			this.type = options.type || 'default';
			this.position = options.position || 'center';
			this.duration = options.duration !== undefined ? options.duration : 2000;
			this.showIcon = options.showIcon !== false;
			this.customStyle = options.style || {};

			// 显示
			this.visible = true;

			// 自动隐藏（loading 类型不自动隐藏）
			if (this.duration > 0 && this.type !== 'loading') {
				this.timer = setTimeout(() => {
					this.hide();
				}, this.duration);
			}
		},

		hide() {
			this.visible = false;
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}
		},

		// 快捷方法
		success(message, duration) {
			this.show({ message, type: 'success', duration });
		},

		error(message, duration) {
			this.show({ message, type: 'error', duration });
		},

		warning(message, duration) {
			this.show({ message, type: 'warning', duration });
		},

		info(message, duration) {
			this.show({ message, type: 'default', duration });
		},

		loading(message, duration = 0) {
			this.show({ message, type: 'loading', duration, showIcon: true });
		}
	}
};
</script>

<style lang="scss" scoped>
// 变量定义
$toast-bg: rgba(0, 0, 0, 0.75);
$toast-blur: 8px;
$toast-radius: 12px;
$toast-padding: 12px 20px;
$toast-min-width: 120px;
$toast-max-width: 80%;
$toast-color: #ffffff;
$toast-font-size: 14px;

// 颜色
$success-color: #52c41a;
$error-color: #ff4d4f;
$warning-color: #faad14;

.global-toast {
	position: fixed;
	z-index: 10000;
	max-width: $toast-max-width;
	min-width: $toast-min-width;
	background: $toast-bg;
	backdrop-filter: blur($toast-blur);
	border-radius: $toast-radius;
	padding: $toast-padding;
	color: $toast-color;
	font-size: $toast-font-size;
	text-align: center;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	pointer-events: none;
	line-height: 1.4;
	word-break: break-word;

	&.default {
		min-height: auto !important;
	}

	// 位置样式
	&.center {
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	&.top {
		top: 15%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	&.bottom {
		bottom: 15%;
		left: 50%;
		transform: translate(-50%, 50%);
	}

	// 类型样式
	&.success .toast-icon {
		color: $success-color;
	}

	&.error .toast-icon {
		color: $error-color;
	}

	&.warning .toast-icon {
		color: $warning-color;
	}
}

.toast-content {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	flex-wrap: wrap;
}

.toast-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
	font-style: normal;
	font-weight: bold;

	// 成功图标
	&.icon-success {
		&::before {
			content: '✓';
			font-size: 18px;
		}
	}

	// 错误图标
	&.icon-error {
		&::before {
			content: '✗';
			font-size: 18px;
		}
	}

	// 警告图标
	&.icon-warning {
		&::before {
			content: '!';
			font-size: 18px;
			font-weight: bold;
		}
	}

	// 加载图标
	&.icon-loading {
		width: 18px;
		height: 18px;
		border: 2px solid rgba(255, 255, 255, 0.3);
		border-top-color: #ffffff;
		border-radius: 50%;
		animation: toast-spin 0.8s linear infinite;

		&::before {
			display: none;
		}
	}
}

.toast-text {
	max-width: 100%;
}

// 动画
@keyframes toast-spin {
	to {
		transform: rotate(360deg);
	}
}

// 过渡动画
.toast-fade-enter-active,
.toast-fade-leave-active {
	transition: all 0.3s ease;
}

.toast-fade-enter,
.toast-fade-leave-to {
	opacity: 0;

	&.center {
		transform: translate(-50%, -50%) scale(0.9);
	}

	&.top {
		transform: translate(-50%, -50%) scale(0.9);
	}

	&.bottom {
		transform: translate(-50%, 50%) scale(0.9);
	}
}
</style>
